import { DecimalSchema } from "../../decimal";
import { ArenaChatMessageSchema, ArenaChatModerationEventSchema } from "../cede-arena/arena-chat.dto";
import { BattleRoyaleStateDtoSchema } from "../cede-arena/battle-royale";
import { SignedDecimalStringSchema } from "../cede-arena/decimal-string";
import { OpponentActivitySsePayloadSchema } from "../cede-arena/opponent-activity.dto";
import { TradeToEarnLeaderboardWireSchema } from "../leaderboard/tte-leaderboard.dto";
import { z } from "zod";

const PnlSnapshotBaseSchema = z.object({
  id: z.string().optional(),
  campaignName: z.string(),
  epochId: z.number(),
  pnl: DecimalSchema,
  score: DecimalSchema,
  tradedVolumeUSD: DecimalSchema,
  createdAt: z.coerce.date(),
});

// Trade-driven PnL snapshot: emitted only when a user's position changes (buy/sell),
// never for price-driven mark-to-market. Clients reconstruct the current unrealized
// PnL by combining these snapshots with live price events.
const PnlSnapshotRecordSchema = PnlSnapshotBaseSchema.extend({
  pendingTokenAmount: DecimalSchema,
  /**
   * Cost basis of the pending tokens in USD — what the user originally paid
   * to acquire the open position (sum of `volumeUSD` across pending BUY lots
   * for spot, sum of entry-lot USD cost for perps).
   */
  pendingTokenPurchasePriceUSD: DecimalSchema,
  tokenPair: z.string(),
});

/**
 * Served publicly while an Arena epoch is live: no market, position or volume (each fill's notional is its step), only
 * the user's total unrealized PnL at the snapshot's time, in cents like the LIVE_PNL frames. The viewer's own full
 * snapshots come from `/me`.
 */
const RedactedPnlSnapshotRecordSchema = PnlSnapshotBaseSchema.omit({ score: true, tradedVolumeUSD: true }).extend({
  unrealizedPnlUsd: DecimalSchema,
});

const PnlSnapshotWireRecordSchema = z.union([PnlSnapshotRecordSchema, RedactedPnlSnapshotRecordSchema]);

/**
 * Internal API shape for a persisted market price snapshot. Keeps every
 * column the legacy leaderboard rules / BullMQ jobs / archival writers
 * depend on (`id` as deterministic BullMQ jobId, `exchangeId` for venue
 * routing). Never leaves the API.
 */
const MarketPriceSnapshotDtoSchema = z.object({
  id: z.string(),
  pairSymbol: z.string(),
  exchangeId: z.string(),
  priceUSD: DecimalSchema,
  createdAt: z.coerce.date(),
});

/**
 * Wire shape for a single price event (SSE payload + the atom inside the
 * columnar bulk response). `id` and `exchangeId` are stripped — they're
 * redundant on the wire (the endpoint is already filtered by exchangeId and
 * clients don't reference event ids). `createdAt` accepts a number (ms since
 * epoch) or ISO string and coerces to `Date` on the consumer.
 */
const PriceEventSchema = z.object({
  pairSymbol: z.string(),
  priceUSD: DecimalSchema,
  createdAt: z.coerce.date(),
});

/**
 * Columnar bulk representation grouped by `pairSymbol`. One entry per pair
 * with parallel `prices` / `timestamps` arrays. This collapses the per-event
 * JSON-key overhead (which dominates the payload at 12h × 5s cadence — 8640
 * events per pair) and uses numeric ms timestamps instead of ISO strings.
 */
const PriceEventSeriesSchema = z.object({
  pairSymbol: z.string(),
  prices: z.array(DecimalSchema),
  timestamps: z.array(z.number()),
});

const UserSnapshotsSchema = z.object({
  /**
   * Display and join field only, NOT identity. It is nullable on `HubUser`
   * (email-OTP accounts link no wallet), so each producer substitutes its own
   * placeholder and the spellings differ — key on `hubUserId`.
   */
  defiAddress: z.string(),
  nickname: z.string().nullable().optional(),
  /**
   * The participant's identity, and what consumers key rows by. Required so a
   * producer cannot ship a payload that forces a consumer back onto the
   * address. Also surfaces `Player #<hubUserId>` when no nickname is set.
   */
  hubUserId: z.number().int(),
  snapshots: z.array(PnlSnapshotRecordSchema),
});

/** A server mark of one redacted player's latest snapshot positions at a REST read. */
const UnrealizedPnlReadingSchema = z.object({
  hubUserId: z.number().int(),
  /** USD, rounded to the cent. */
  unrealizedPnlUsd: SignedDecimalStringSchema,
  /** The snapshot this reading marks; a client applies it only when that is also its latest. */
  basisSnapshotId: z.string().min(1),
});

const UserSnapshotsWireSchema = UserSnapshotsSchema.extend({
  snapshots: z.array(PnlSnapshotWireRecordSchema),
  /** Redacted users only: their unrealized PnL at the time of the read, so a refetch does not rewind their total. */
  unrealizedNow: UnrealizedPnlReadingSchema.extend({ at: z.iso.datetime() }).optional(),
});

export const PnlSnapshotResponseSchema = z.object({
  users: z.array(UserSnapshotsWireSchema),
});

// Discriminant for the leaderboard ROOM stream envelope. Despite the
// `PnlSnapshot*` name, this union multiplexes every per-(campaign, epoch) live
// feed onto one EventSource: PnL/price, leaderboard rankings, AND arena chat.
// Each producing module reaches the leaderboard route via ApiSync + an in-proc
// surface; the client opens ONE socket per room instead of one per feed (the
// browser caps ~6 HTTP/1.1 sockets/origin and SSE holds them open).
export enum PnlSnapshotSseEventType {
  PNL_SNAPSHOT = "pnlSnapshot",
  PRICE_EVENT = "priceEvent",
  LEADERBOARD = "leaderboard",
  // Arena in-competition chat, merged in from the cede-arena-chat module so the
  // battle page doesn't open a separate chat socket. A posted message and a
  // moderation ban ride distinct variants (old clients ignore unknown types).
  CHAT_MESSAGE = "chatMessage",
  CHAT_MODERATION = "chatModeration",
  // Redacted per-fill activity of every Arena player (no market, side, size or price).
  OPPONENT_ACTIVITY = "opponentActivity",
  // Arena players' total PnL, kept in memory from their fills and marked every
  // 500ms while the epoch is live (their positions are withheld from the room). Partial frames.
  LIVE_PNL = "livePnl",
  // A Battle Royale cut, sent right after its record commits, with the room's BR state after it.
  BATTLE_ROYALE_ELIMINATION = "battleRoyaleElimination",
  // The competition's results are final (same signal as the ARENA_COMPETITION_FINALIZED notification).
  COMPETITION_FINALIZED = "competitionFinalized",
  // The battle record's build is over, stored or given up: a read answered RECORD_PENDING is made once more.
  BATTLE_RECORD_DONE = "battleRecordDone",
  // A server-side feed of this room errored and was resubscribed; frames may
  // have been lost, so the client re-reads that feed's state.
  RESYNC = "resync",
}

/**
 * Liveness tick on the room stream, sent as a named SSE event (`event: ping`) so
 * it never reaches a `message` listener: clients without a watchdog ignore it.
 */
export const PNL_SNAPSHOT_SSE_PING_EVENT = "ping";
export const PNL_SNAPSHOT_SSE_PING_INTERVAL_MS = 5_000;

const PnlSnapshotSsePayloadSchema = z.object({
  campaignName: z.string(),
  epochId: z.number(),
  users: z.array(UserSnapshotsWireSchema),
});

/** One reading on the wire: `[hubUserId, totalPnlCents, realizedPnlCents]`, integer cents; unrealized = total - realized. */
export const LivePnlFrameReadingSchema = z.tuple([z.number().int(), z.number().int(), z.number().int()]);

/**
 * A live Arena room's PnL per player: realized from their fills as they happen, plus open positions marked every
 * 500ms at the latest paper prices (live Arena only, instead of PRICE_EVENT frames, which would name the held
 * markets). Partial: only readings that changed since the room's last frame, plus every priced player on a keyframe
 * (every 10th frame). A player absent from a partial frame keeps their last reading; one absent from a keyframe has no
 * fresh price, so their last reading is no longer current. Players who never traded or were eliminated are never sent.
 */
export const LivePnlSsePayloadSchema = z.object({
  at: z.iso.datetime(),
  isKeyframe: z.boolean(),
  players: z.array(LivePnlFrameReadingSchema),
});

/** One Battle Royale cut as recorded; `skipped` = consumed by an earlier multi-player cut, no players. */
export const BattleRoyaleEliminationSsePayloadSchema = z.object({
  campaignName: z.string(),
  eliminationIndex: z.number().int().nonnegative(),
  executedAt: z.iso.datetime(),
  skipped: z.boolean(),
  /** Worst first; `pnlUsd` is the PnL frozen at the cut, null = never traded. */
  players: z.array(z.object({ hubUserId: z.number().int(), pnlUsd: SignedDecimalStringSchema.nullable() })),
  /** `GET v1/cede-arena/battle-royale/:name` as of this cut's commit; null when the server could not read it. */
  state: BattleRoyaleStateDtoSchema.nullable(),
});

const CompetitionFinalizedSsePayloadSchema = z.object({
  campaignName: z.string(),
  epochId: z.number(),
});

const BattleRecordDoneSsePayloadSchema = z.object({
  campaignName: z.string(),
  epochId: z.number(),
});

export const PnlRoomFeedSchema = z.enum([
  "pnl",
  "market-price",
  "live-pnl",
  "opponent-activity",
  "battle-royale",
  "competition-finalized",
]);
export type PnlRoomFeed = z.infer<typeof PnlRoomFeedSchema>;

// A feed this bundle predates parses as "unknown" and re-reads nothing: a tab outlives a server that adds feeds.
const ResyncSsePayloadSchema = z.object({
  feed: z.string().transform((feed): PnlRoomFeed | "unknown" => {
    const known = PnlRoomFeedSchema.safeParse(feed);
    return known.success ? known.data : "unknown";
  }),
});

const PriceEventSsePayloadSchema = z.object({
  priceEvent: PriceEventSchema,
});

const LeaderboardSsePayloadSchema = z.object({
  leaderboard: TradeToEarnLeaderboardWireSchema,
});

const ChatMessageSsePayloadSchema = z.object({
  message: ArenaChatMessageSchema,
});

const ChatModerationSsePayloadSchema = z.object({
  moderation: ArenaChatModerationEventSchema,
});

export const PnlSnapshotSseEventSchema = z.discriminatedUnion("type", [
  z.object({ type: z.literal(PnlSnapshotSseEventType.PNL_SNAPSHOT), payload: PnlSnapshotSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.PRICE_EVENT), payload: PriceEventSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.LEADERBOARD), payload: LeaderboardSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.CHAT_MESSAGE), payload: ChatMessageSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.CHAT_MODERATION), payload: ChatModerationSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.OPPONENT_ACTIVITY), payload: OpponentActivitySsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.LIVE_PNL), payload: LivePnlSsePayloadSchema }),
  z.object({
    type: z.literal(PnlSnapshotSseEventType.BATTLE_ROYALE_ELIMINATION),
    payload: BattleRoyaleEliminationSsePayloadSchema,
  }),
  z.object({
    type: z.literal(PnlSnapshotSseEventType.COMPETITION_FINALIZED),
    payload: CompetitionFinalizedSsePayloadSchema,
  }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.BATTLE_RECORD_DONE), payload: BattleRecordDoneSsePayloadSchema }),
  z.object({ type: z.literal(PnlSnapshotSseEventType.RESYNC), payload: ResyncSsePayloadSchema }),
]);

export const UserPnlStatsResponseSchema = z.object({
  userCount: z.number(),
  rank: z.number().nullable(),
  snapshots: z.array(PnlSnapshotRecordSchema),
});

export const PriceEventsResponseSchema = z.object({
  priceEvents: z.array(PriceEventSeriesSchema),
});

export type PnlSnapshotRecordDto = z.infer<typeof PnlSnapshotRecordSchema>;
export type RedactedPnlSnapshotRecordDto = z.infer<typeof RedactedPnlSnapshotRecordSchema>;
export type PnlSnapshotWireRecordDto = z.infer<typeof PnlSnapshotWireRecordSchema>;
export type UserSnapshotsDto = z.infer<typeof UserSnapshotsSchema>;
export type UserSnapshotsWireDto = z.infer<typeof UserSnapshotsWireSchema>;
export type PnlSnapshotResponseDto = z.infer<typeof PnlSnapshotResponseSchema>;
export type PnlSnapshotSseEventDto = z.infer<typeof PnlSnapshotSseEventSchema>;
export type LivePnlSsePayloadDto = z.infer<typeof LivePnlSsePayloadSchema>;
export type LivePnlFrameReadingDto = z.infer<typeof LivePnlFrameReadingSchema>;
export type BattleRoyaleEliminationSsePayloadDto = z.infer<typeof BattleRoyaleEliminationSsePayloadSchema>;
export type UserPnlStatsResponseDto = z.infer<typeof UserPnlStatsResponseSchema>;
export type MarketPriceSnapshotDto = z.infer<typeof MarketPriceSnapshotDtoSchema>;
export type PriceEventDto = z.infer<typeof PriceEventSchema>;
export type PriceEventSeriesDto = z.infer<typeof PriceEventSeriesSchema>;
export type PriceEventsResponseDto = z.infer<typeof PriceEventsResponseSchema>;

// Client-only: same as the trade-driven snapshot but with client-derived
// `unrealizedPnl` re-injected by the frontend reducer after applying the
// latest known price event for the pair. Components should consume this
// shape; wire protocol never carries `unrealizedPnl`.
export type HydratedPnlSnapshot = PnlSnapshotRecordDto & {
  /** Cross-pair total unrealized PnL at this moment (FE reducer injects it). */
  unrealizedPnl: PnlSnapshotRecordDto["pnl"];
  /** This snapshot's pair cumulative realized PnL (FE-reconstructed from
   * cumulative-`pnl` deltas grouped by tokenPair). */
  pairRealizedPnl?: PnlSnapshotRecordDto["pnl"];
  /** This snapshot's pair unrealized PnL at this moment (FE-computed). */
  pairUnrealizedPnl?: PnlSnapshotRecordDto["pnl"];
};

export const isRedactedPnlSnapshot = (snapshot: PnlSnapshotWireRecordDto): snapshot is RedactedPnlSnapshotRecordDto =>
  !("tokenPair" in snapshot);

/**
 * Narrows wire users to full snapshots, for surfaces that never receive
 * redacted ones (Hub campaigns are never redacted). Throws rather than guess.
 */
export const requireFullUserSnapshots = (users: UserSnapshotsWireDto[]): UserSnapshotsDto[] =>
  users.map((user) => ({
    ...user,
    snapshots: user.snapshots.map((snapshot) => {
      if (isRedactedPnlSnapshot(snapshot)) {
        throw new Error(`Unexpected redacted PnL snapshot for hubUserId ${user.hubUserId}`);
      }
      return snapshot;
    }),
  }));
